"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Shutter, { type ShutterPhase } from "@/components/shared/Shutter";

/**
 * Single global shutter controller mounted at the app root.
 *
 * Both the locale switcher and `TransitionLink` previously owned their
 * own shutter overlays. That meant the animation could be killed
 * mid-flight whenever the *source* React subtree unmounted (e.g. during
 * a fast route change), so the user would see the panel pop out instead
 * of finishing its open animation.
 *
 * This component lives *above* the route boundary, so its `phase` state
 * and timers are never unmounted by navigation. The close → covered →
 * (action) → opening → idle cycle is therefore guaranteed to play in
 * full regardless of how quickly the new page resolves.
 *
 * Consumers drive it via {@link useShutter}:
 *
 *   const { runShutter } = useShutter();
 *   runShutter(() => router.push(href));
 */
export type ShutterRunOptions = {
  /**
   * Optional minimum time (ms) the shutter should stay fully covering
   * before starting to open. Useful when the wrapped action triggers an
   * async render that needs a beat to settle (server actions, route
   * change with streaming SSR, etc.). Defaults to {@link SHUTTER_HOLD_MS}.
   */
  holdMs?: number;
};

type ShutterContextValue = {
  phase: ShutterPhase;
  /**
   * Run an action behind a guaranteed full shutter cycle. Calls during
   * an in-flight cycle are ignored, so rapid double-clicks can't desync
   * the animation.
   */
  runShutter: (action: () => void, options?: ShutterRunOptions) => void;
};

const ShutterContext = createContext<ShutterContextValue | null>(null);

// Must mirror the CSS transition duration in `Shutter.tsx` (currently
// 420ms). If you tweak the visual transition, keep these in sync.
const SHUTTER_SLIDE_MS = 420;
const SHUTTER_HOLD_MS = 320;

export default function ShutterController({
  children,
}: {
  children: React.ReactNode;
}) {
  const [phase, setPhase] = useState<ShutterPhase>("idle");

  // Track in-flight timers so we can clean them up on unmount (only
  // happens on a full app teardown — fast-refresh, etc.). Phase + timers
  // intentionally do **not** reset on every render so the cycle is
  // resilient to children unmounting underneath.
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    };
  }, []);

  // Use a ref mirror of `phase` so `runShutter` doesn't need `phase` in
  // its dep list — keeping the callback stable across re-renders means
  // descendants don't re-render every time the phase changes.
  const phaseRef = useRef<ShutterPhase>("idle");
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const runShutter = useCallback(
    (action: () => void, options?: ShutterRunOptions) => {
      if (phaseRef.current !== "idle") return;
      const holdMs = options?.holdMs ?? SHUTTER_HOLD_MS;

      // Phase 1 — close: shutter slides DOWN to fully cover the viewport.
      setPhase("closing");
      phaseRef.current = "closing";

      // Phase 2 — at the end of the close, run the action behind the
      // covering panel and flip into the held "covered" state.
      const t1 = setTimeout(() => {
        try {
          action();
        } catch (err) {
          // Swallow + log: the animation must always complete even if
          // the wrapped action throws. The caller can surface its own
          // error UI separately.
          console.error("[ShutterController] action threw:", err);
        }
        setPhase("covered");
        phaseRef.current = "covered";
      }, SHUTTER_SLIDE_MS);

      // Phase 3 — small hold so the action's side-effects (e.g. server
      // streaming, router.refresh) get a beat to settle behind the
      // curtain.
      const t2 = setTimeout(() => {
        setPhase("opening");
        phaseRef.current = "opening";
      }, SHUTTER_SLIDE_MS + holdMs);

      // Phase 4 — open complete; reset the machine.
      const t3 = setTimeout(() => {
        setPhase("idle");
        phaseRef.current = "idle";
      }, SHUTTER_SLIDE_MS * 2 + holdMs);

      timersRef.current.push(t1, t2, t3);
    },
    []
  );

  const value = useMemo<ShutterContextValue>(
    () => ({ phase, runShutter }),
    [phase, runShutter]
  );

  return (
    <ShutterContext.Provider value={value}>
      {children}
      <Shutter phase={phase} />
    </ShutterContext.Provider>
  );
}

export const useShutter = (): ShutterContextValue => {
  const ctx = useContext(ShutterContext);
  if (!ctx) {
    throw new Error("useShutter must be used inside <ShutterController>");
  }
  return ctx;
};
